import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { Skeleton } from "@/components/ui/skeleton";
import { kindIcon } from "@/pages/coach/methods/shared";
import { CheckCircle2, Eye, BarChart3 } from "lucide-react";

export default function StatsTab({ onEdit }) {
  const t = useT();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("completed");

  const load = useCallback(async () => {
    try { const { data: d } = await api.get("/coach/methods/stats/summary"); setData(d); }
    catch { setData({ total_methods: 0, top_completed: [], top_opened: [] }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (data === null) return <div className="space-y-3" data-testid="cm-stats-tab">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>;

  const list = data[tab === "completed" ? "top_completed" : "top_opened"] || [];
  const maxVal = Math.max(1, ...list.map((r) => (tab === "completed" ? r.completions_count : r.opens_count) || 0));

  return (
    <div className="space-y-5" data-testid="cm-stats-tab">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi label={t("cm.stats_methods")} value={data.total_methods ?? 0} testId="cm-stats-methods" />
        <Kpi label={t("cm.stats_opens")} value={data.total_opens ?? 0} testId="cm-stats-opens" />
        <Kpi label={t("cm.stats_completions")} value={data.total_completions ?? 0} testId="cm-stats-completions" />
        <Kpi label={t("cm.stats_rate")} value={`${data.completion_rate_pct ?? 0}%`} accent testId="cm-stats-rate" />
      </div>

      <div className="flex gap-1 rounded-full bg-secondary p-1">
        <button onClick={() => setTab("completed")} data-testid="cm-stats-tab-completed" className={`flex flex-1 items-center justify-center gap-1.5 rounded-full py-2 text-sm font-medium transition-colors ${tab === "completed" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
          <CheckCircle2 className="h-4 w-4" /> {t("cm.stats_top_completed")}
        </button>
        <button onClick={() => setTab("opened")} data-testid="cm-stats-tab-opened" className={`flex flex-1 items-center justify-center gap-1.5 rounded-full py-2 text-sm font-medium transition-colors ${tab === "opened" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
          <Eye className="h-4 w-4" /> {t("cm.stats_top_opened")}
        </button>
      </div>

      {list.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><BarChart3 className="h-6 w-6" /></div>
          <p className="mt-3 text-sm text-muted-foreground">{t("cm.stats_empty")}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((m, idx) => {
            const val = (tab === "completed" ? m.completions_count : m.opens_count) || 0;
            const Icon = kindIcon(m.kind);
            return (
              <button key={m.id} data-testid={`cm-stats-row-${m.id}`} onClick={() => onEdit?.(m.id)} className="flex w-full items-center gap-3 rounded-xl border border-border bg-card p-3 text-left hover:bg-accent">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">#{idx + 1}</div>
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary"><Icon className="h-4 w-4 text-primary" /></div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-foreground">{m.title}</p>
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-secondary">
                    <div className="h-full rounded-full bg-primary" style={{ width: `${Math.max(2, Math.round((val / maxVal) * 100))}%` }} />
                  </div>
                  <div className="mt-1 flex gap-3 text-[11px] text-muted-foreground">
                    <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> {m.completions_count}</span>
                    <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {m.opens_count}</span>
                  </div>
                </div>
                <p className="font-display text-2xl font-semibold text-primary">{val}</p>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, accent, testId }) {
  return (
    <div className={`rounded-2xl border p-4 text-center ${accent ? "border-primary/40 bg-primary/5" : "border-border bg-card"}`} data-testid={testId}>
      <p className={`font-display text-2xl font-semibold ${accent ? "text-primary" : "text-foreground"}`}>{value}</p>
      <p className="mt-0.5 text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
    </div>
  );
}
